/*
 * <mikolito-menu-banners> — rotating "arcade coupon" promo banners that take the
 * place of the featured-product cards in the header mega-menu (desktop) and the
 * mobile menu drawer.
 *
 * Banner content + behaviour are authored as `_mikolito-menu-banner` theme blocks
 * under the header menu block. Those render once into a hidden
 * `#MikolitoMenuBannersData` container in the header DOM; every
 * <mikolito-menu-banners> mount reads that container and builds its own carousel,
 * so the desktop and drawer instances run independently.
 *
 * Hydration runs from connectedCallback, backed by a short boot poll and a
 * MutationObserver on the header — see the comment above the boot code below
 * for why the header's <overflow-list> makes both of those necessary.
 */

const DATA_ID = 'MikolitoMenuBannersData';

class MikolitoMenuBanners extends HTMLElement {
  connectedCallback() {
    hydrate(this);
  }

  disconnectedCallback() {
    this._stop();
    if (this._io) this._io.disconnect();
  }

  /* ---- hydration ------------------------------------------------------- */

  _hydrate() {
    if (this.dataset.hydrated === 'true') return;

    const source = document.getElementById(DATA_ID);
    if (!source) return;

    const banners = readBanners(source);
    if (banners.length === 0) return;

    this.banners = banners;
    this.context = this.dataset.context === 'drawer' ? 'drawer' : 'mega';
    this.speed = parseInt(source.dataset.rotationSpeed, 10) || 5000;
    this.showArrows = source.dataset.showArrows !== 'false';
    this.showDots = source.dataset.showDots !== 'false';

    this.index = 0;
    this.paused = false;
    this.visible = true;
    this.timer = null;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.dataset.hydrated = 'true';

    this._render();
    this._bindEvents();
    this._observeVisibility();

    if (this.banners.length > 1) this._start();
  }

  /* ---- markup -------------------------------------------------------- */

  _render() {
    const total = this.banners.length;
    const multi = total > 1;

    this.classList.add('mmb', `mmb--${this.context}`);

    const arrows =
      multi && this.showArrows
        ? `<button type="button" class="mmb__arrow mmb__arrow--prev" aria-label="Poprzedni baner">&lsaquo;</button>
           <button type="button" class="mmb__arrow mmb__arrow--next" aria-label="Następny baner">&rsaquo;</button>`
        : '';

    const dots =
      multi && this.showDots
        ? `<div class="mmb__dots" role="tablist">${this.banners
            .map(
              (_, i) =>
                `<button type="button" class="mmb__dot" role="tab" aria-label="Baner ${i + 1} z ${total}"${
                  i === 0 ? ' aria-selected="true"' : ''
                } data-i="${i}"></button>`
            )
            .join('')}</div>`
        : '';

    this.innerHTML = `
      <div class="mmb__viewport">
        ${arrows}
        <div class="mmb__slot" aria-live="polite">${this._slide(this.banners[0], 0, total)}</div>
      </div>
      ${dots}
    `;

    this.slotEl = this.querySelector('.mmb__slot');
    this.dotEls = Array.from(this.querySelectorAll('.mmb__dot'));
  }

  _slide(b, i, total) {
    const pad = (v) => String(v).padStart(2, '0');
    const code =
      this.context === 'drawer'
        ? `${pad(i + 1)} / ${pad(total)}`
        : `BILET ${pad(i + 1)} / ${pad(total)}`;
    const withDesc = this.context === 'mega';

    const barcode = withDesc
      ? `<span class="mmb__barcode" aria-hidden="true">${'<i></i>'.repeat(9)}</span>`
      : '';
    const desc = withDesc && b.desc ? `<p class="mmb__desc">${esc(b.desc)}</p>` : '';
    const tag = withDesc && b.tag ? `<span class="mmb__tag">${esc(b.tag)}</span>` : '';

    return `
      <div class="mmb__coupon" style="--mmb-stub:${cssColor(b.stub)};--mmb-bg:${cssColor(b.bg)};">
        <div class="mmb__inner">
          <div class="mmb__head">
            <span class="mmb__badge-wrap">
              ${b.badge ? `<span class="mmb__badge">${esc(b.badge)}</span>` : ''}
              ${b.eyebrow ? `<span class="mmb__eyebrow">${esc(b.eyebrow)}</span>` : ''}
            </span>
            <span class="mmb__meta">
              <span class="mmb__code">${esc(code)}</span>
              ${barcode}
            </span>
          </div>
          <div class="mmb__perf" aria-hidden="true"></div>
          ${b.title ? `<div class="mmb__title">${esc(b.title)}</div>` : ''}
          ${desc}
          <div class="mmb__cta-row">
            ${
              b.cta && b.link
                ? `<a class="mmb__cta" href="${esc(b.link)}">${esc(b.cta)}</a>`
                : b.cta
                ? `<span class="mmb__cta mmb__cta--static">${esc(b.cta)}</span>`
                : ''
            }
            ${tag}
          </div>
        </div>
      </div>
    `;
  }

  /* ---- behaviour --------------------------------------------------- */

  _bindEvents() {
    // Guard against the same click reaching this handler twice (it was observed
    // advancing two slides per tap, i.e. arrows looked dead) — handle each
    // event object at most once, and bind only once per element.
    if (this._bound) return;
    this._bound = true;

    this.addEventListener('click', (e) => {
      if (e._mmbHandled) return;
      e._mmbHandled = true;
      const arrow = e.target.closest('.mmb__arrow');
      if (arrow) {
        this._manualGo(arrow.classList.contains('mmb__arrow--next') ? this.index + 1 : this.index - 1);
        return;
      }
      const dot = e.target.closest('.mmb__dot');
      if (dot) this._manualGo(parseInt(dot.dataset.i, 10));
    });

    // Swipe left/right on touch devices.
    let touchX = null;
    this.addEventListener('touchstart', (e) => {
      touchX = e.touches[0].clientX;
    }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (touchX === null) return;
      const dx = e.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) > 40) this._manualGo(this.index + (dx < 0 ? 1 : -1));
    }, { passive: true });

    const pause = () => {
      this.paused = true;
    };
    const resume = () => {
      this.paused = false;
    };
    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
  }

  _observeVisibility() {
    if (!('IntersectionObserver' in window)) return;
    this._io = new IntersectionObserver(
      (entries) => {
        this.visible = entries[0].isIntersecting;
      },
      { threshold: 0.01 }
    );
    this._io.observe(this);
  }

  // Manual navigation (arrow / dot / swipe): also restart the auto-rotate
  // countdown so the new banner gets its full display time.
  _manualGo(next) {
    this._go(next);
    if (this.banners.length > 1) this._start();
  }

  _go(next) {
    const total = this.banners.length;
    this.index = ((next % total) + total) % total;

    this.slotEl.innerHTML = this._slide(this.banners[this.index], this.index, total);
    this.dotEls.forEach((d, i) => {
      if (i === this.index) d.setAttribute('aria-selected', 'true');
      else d.removeAttribute('aria-selected');
    });
  }

  _start() {
    if (this.reduceMotion) return;
    this._stop();
    this.timer = window.setInterval(() => {
      if (!this.paused && this.visible) this._go(this.index + 1);
    }, this.speed);
  }

  _stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }
}

/* ---- module helpers ------------------------------------------------- */

function readBanners(source) {
  return Array.from(source.querySelectorAll('.mikolito-menu-banner-def'))
    .map((el) => ({
      badge: el.dataset.badge || '',
      eyebrow: el.dataset.eyebrow || '',
      title: el.dataset.title || '',
      desc: el.dataset.desc || '',
      cta: el.dataset.cta || '',
      link: el.dataset.link || '',
      stub: el.dataset.stub || '#131A33',
      bg: el.dataset.bg || '#E9ECF6',
      tag: el.dataset.tag || '',
    }))
    .filter((b) => b.title || b.eyebrow || b.cta);
}

function hydrate(el) {
  if (el instanceof MikolitoMenuBanners) el._hydrate();
}

function hydrateAll(root) {
  (root || document).querySelectorAll('mikolito-menu-banners').forEach(hydrate);
}

function esc(str) {
  return String(str).replace(/[&<>"']/g, (c) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

// Allow only hex / rgb(a) / hsl(a) / named colours through into the style attr.
function cssColor(value) {
  const v = String(value).trim();
  return /^#[0-9a-f]{3,8}$/i.test(v) ||
    /^(rgb|hsl)a?\([0-9.,%\s/]+\)$/i.test(v) ||
    /^[a-z]+$/i.test(v)
    ? v
    : '#131A33';
}

if (!customElements.get('mikolito-menu-banners')) {
  customElements.define('mikolito-menu-banners', MikolitoMenuBanners);
}

function allHydrated() {
  const mounts = document.querySelectorAll('mikolito-menu-banners');
  // Vacuously "done" only counts once mounts actually exist — otherwise a
  // poll starting before they're connected would stop before ever trying.
  return mounts.length > 0 && Array.from(mounts).every((el) => el.dataset.hydrated === 'true');
}

/*
 * The mounts sit inside the header's <overflow-list> web component, which
 * measures its content once after first connecting and rebuilds its subtree
 * as part of that — replacing the initial (already-hydrated) mounts with
 * fresh, un-upgraded ones. connectedCallback fires normally for both the
 * first and the rebuilt instances, so a MutationObserver on the header
 * catches the swap; the short poll below is just a safety net for the very
 * first paint. Hydration is idempotent (guarded by data-hydrated), so none
 * of this can double-render.
 */
hydrateAll();
let attempts = 0;
const poll = window.setInterval(() => {
  hydrateAll();
  attempts += 1;
  // Poll every 100ms for up to ~14s (stops as soon as everything is hydrated).
  if (allHydrated() || attempts >= 140) window.clearInterval(poll);
}, 100);

// Timing-independent safety net: any user interaction or page-lifecycle event
// hydrates anything still pending. Capture phase, so it runs before the
// mega-menu / drawer opens and the user can see an empty mount.
['load', 'pageshow', 'visibilitychange'].forEach((type) => window.addEventListener(type, () => hydrateAll()));
['pointerover', 'pointerdown', 'touchstart', 'click', 'focusin', 'keydown'].forEach((type) =>
  document.addEventListener(type, () => hydrateAll(), { capture: true, passive: true })
);

const observerRoot = document.getElementById('header-component') || document.body;
if (observerRoot && 'MutationObserver' in window) {
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('mikolito-menu-banners')) hydrate(node);
        node.querySelectorAll?.('mikolito-menu-banners').forEach(hydrate);
      }
    }
  }).observe(observerRoot, { childList: true, subtree: true });
}
